import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileSpreadsheet, FileText, Plus, Trash2, Search, Loader2, X, Download,
  Package, Sparkles, Tag, Building2, Database, ChevronDown, RefreshCw,
} from "lucide-react";
import { API } from "../contexts/AuthContext";

const opts = { withCredentials: true };

// Pedido video 3 #2 + video 4 #5: categorías sugeridas (incluye Mano de Obra)
const DEFAULT_CATEGORIES = [
  "Cables", "Bandejas", "Mecanismos", "Disyuntores",
  "Iluminación", "Tableros", "Tuberías", "Accesorios",
  "Mano de Obra", "Otros"
];

const fmtPYG = (n) => `Gs. ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`;

// ─── Import Modal ──────────────────────────────────────────────────────────────
function ImportModal({ open, onClose, onImported }) {
  const [mode, setMode] = useState("excel"); // excel | pdf
  const [proveedor, setProveedor] = useState("");
  const [categoria, setCategoria] = useState("Cables");
  const [sheetsAsBrands, setSheetsAsBrands] = useState(true);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!proveedor.trim()) { toast.error("Ingresá el nombre del proveedor"); return; }
    if (!file) { toast.error("Seleccioná un archivo"); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("proveedor", proveedor.trim());
    fd.append("categoria", categoria || "");
    fd.append("file", file);
    try {
      let res;
      if (mode === "excel") {
        fd.append("sheets_as_brands", String(sheetsAsBrands));
        res = await axios.post(`${API}/budgets/price-lists/import-excel-ia`, fd, { ...opts, headers: { "Content-Type": "multipart/form-data" } });
      } else {
        res = await axios.post(`${API}/budgets/price-lists/import-pdf`, fd, { ...opts, headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(res.data.message || "Importación exitosa");
      onImported();
      onClose();
      setProveedor(""); setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="import-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Database size={18} className="text-[#2E6DA4]" />
            Importar Lista de Precios
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-5 p-1 bg-gray-100 rounded-xl">
          <button
            onClick={() => setMode("excel")}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
              mode === "excel" ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="mode-excel-btn"
          >
            <FileSpreadsheet size={14} /> Excel (con IA)
            <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold">IA</span>
          </button>
          <button
            onClick={() => setMode("pdf")}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
              mode === "pdf" ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="mode-pdf-btn"
          >
            <FileText size={14} /> PDF (con IA)
            <span className="ml-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[9px] font-bold">NUEVO</span>
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Proveedor *</label>
            <input
              type="text"
              value={proveedor}
              onChange={e => setProveedor(e.target.value)}
              placeholder="ej. Schneider Electric, NEXANS, TESA..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:outline-none"
              data-testid="import-proveedor-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Categoría</label>
            <select
              value={categoria}
              onChange={e => setCategoria(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:outline-none"
              data-testid="import-categoria-select"
            >
              <option value="">— Sin categoría —</option>
              {DEFAULT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {mode === "excel" && (
            <>
              <label className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-100 cursor-pointer hover:bg-emerald-100 transition-colors">
                <input
                  type="checkbox"
                  checked={sheetsAsBrands}
                  onChange={e => setSheetsAsBrands(e.target.checked)}
                  className="mt-0.5 accent-emerald-600"
                  data-testid="import-sheets-as-brands"
                />
                <div>
                  <p className="text-xs font-semibold text-emerald-800">Cada hoja = una marca</p>
                  <p className="text-[11px] text-emerald-700 mt-0.5">
                    Activá esta opción si tu Excel tiene una hoja por marca (NEXANS, CORDEIRO, INDUSCABOS...).
                    Se creará una lista separada por cada hoja.
                  </p>
                </div>
              </label>
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 flex items-start gap-2">
                <Sparkles size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-emerald-800">
                  La IA detecta automáticamente las columnas de descripción, precio, unidad y cantidad — sin importar cómo esté estructurado el Excel.
                  Funciona con cualquier formato de planilla.
                </p>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Archivo {mode === "excel" ? "Excel (.xlsx, .xls)" : "PDF"} *
            </label>
            <input
              type="file"
              accept={mode === "excel" ? ".xlsx,.xls" : ".pdf"}
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#2E6DA4] file:text-white file:font-semibold file:text-xs hover:file:bg-[#245A8A] file:cursor-pointer"
              data-testid="import-file-input"
            />
          </div>

          {mode === "pdf" && (
            <div className="p-3 rounded-xl bg-purple-50 border border-purple-100 flex items-start gap-2">
              <Sparkles size={14} className="text-purple-600 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-purple-800">
                La IA leerá el PDF y extraerá automáticamente descripción, unidad, precio y código de cada producto.
                Funciona mejor con listas estructuradas tipo Schneider, TESA, etc.
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50"
            data-testid="import-cancel-btn"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="import-submit-btn"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> Importando...</> : <>Importar</>}
          </button>
        </div>
      </div>
    </div>
  , document.body);
}

// ─── Items Drawer ──────────────────────────────────────────────────────────────
function ItemsDrawer({ open, proveedor, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !proveedor) return;
    setLoading(true);
    axios.get(`${API}/budgets/price-lists/${encodeURIComponent(proveedor)}/items`, opts)
      .then(r => setData(r.data))
      .catch(err => toast.error(err.response?.data?.detail || "Error al cargar ítems"))
      .finally(() => setLoading(false));
  }, [open, proveedor]);

  if (!open) return null;

  const items = data?.items || [];
  const filtered = !search.trim() ? items :
    items.filter(i =>
      (i.descripcion || "").toLowerCase().includes(search.toLowerCase()) ||
      (i.codigo_proveedor || "").toLowerCase().includes(search.toLowerCase())
    );

  return createPortal(
    <div className="fixed inset-0 z-50 flex" data-testid="items-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#1A4B7A] to-[#2E6DA4] text-white">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Lista de precios</p>
            <h2 className="text-lg font-bold">{proveedor}</h2>
            {data?.updated_at && <p className="text-[11px] opacity-80 mt-0.5">Actualizada: {data.updated_at.slice(0, 10)}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por descripción o código..."
              className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:outline-none"
              data-testid="drawer-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 size={24} className="animate-spin text-[#2E6DA4]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Package size={36} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">{search ? "Sin resultados" : "No hay ítems"}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-gray-100 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Descripción</th>
                  <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Un.</th>
                  <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Cant.</th>
                  <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Precio (Gs.)</th>
                  <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Marca</th>
                  <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Código</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it, i) => (
                  <tr key={i} className={`border-b border-gray-50 hover:bg-[#EBF3FB] ${i % 2 === 0 ? "bg-gray-50/40" : ""}`} data-testid={`item-row-${i}`}>
                    <td className="px-4 py-2 text-xs text-gray-800">{it.descripcion}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{it.unidad}</td>
                    <td className="px-4 py-2 text-xs text-gray-500 text-right tabular-nums">{it.cantidad != null ? it.cantidad : "—"}</td>
                    <td className="px-4 py-2 text-xs font-bold text-[#2E6DA4] text-right tabular-nums">{fmtPYG(it.precio_iva_inc)}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{it.marca || "—"}</td>
                    <td className="px-4 py-2 text-[10px] font-mono text-gray-500">{it.codigo_proveedor || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex justify-between">
          <span>{filtered.length} {filtered.length === 1 ? "ítem" : "ítems"} {search ? "encontrados" : ""}</span>
          {!search && data && <span>{data.items?.length || 0} en total</span>}
        </div>
      </div>
    </div>
  , document.body);
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function MaterialsCatalog() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProveedor, setDrawerProveedor] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  const loadLists = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/budgets/price-lists/all`, opts);
      setLists(res.data || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      const msg = detail
        ? `Error ${status}: ${detail}`
        : err.message || "Error al cargar listas";
      toast.error(msg);
      console.error("[MaterialsCatalog] Error cargando listas:", err.response?.status, err.response?.data, err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLists(); }, []);

  // Pedido video 3 #6: búsqueda global de items para ver disponibilidad
  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (searchQ.trim().length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/budgets/price-lists/search`, { params: { q: searchQ.trim() }, ...opts });
        setSearchResults(res.data || []);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [searchQ]);

  const handleDelete = async (proveedor) => {
    if (!window.confirm(`¿Eliminar la lista "${proveedor}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/budgets/price-lists/${encodeURIComponent(proveedor)}`, opts);
      toast.success("Lista eliminada");
      loadLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const totalItems = lists.reduce((acc, l) => acc + (l.item_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="materials-catalog-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#2E6DA4] font-bold">Ingeniería</p>
          <h1 className="text-3xl font-bold text-gray-900 mt-1">Catálogo de Materiales</h1>
          <p className="text-sm text-gray-500 mt-1">
            Base de datos clasificada de precios por proveedor, marca y categoría — disponible para carga al presupuesto.
          </p>
        </div>
        <button
          onClick={() => setImportOpen(true)}
          className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors"
          data-testid="open-import-btn"
        >
          <Plus size={15} /> Importar lista de precios
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Database size={14} className="text-[#2E6DA4]" />
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Listas</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{lists.length}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Package size={14} className="text-emerald-600" />
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Ítems totales</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">{totalItems.toLocaleString("es-PY")}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Tag size={14} className="text-purple-600" />
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Categorías</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{DEFAULT_CATEGORIES.length}</p>
        </div>
      </div>

      {/* Global Search */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4">
        <label className="block text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Search size={13} /> Buscar precio en todas las listas
        </label>
        <input
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder="Ej: Cable unipolar 1x95 mm2, Disyuntor 1x32A, Bandeja escalera..."
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:outline-none"
          data-testid="global-search-input"
        />
        {searchQ.trim().length >= 2 && (
          <div className="mt-3 max-h-72 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50" data-testid="global-search-results">
            {searching ? (
              <div className="text-center py-4 text-gray-400 text-xs"><Loader2 size={14} className="animate-spin inline mr-1" /> Buscando...</div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-xs">Sin resultados</div>
            ) : searchResults.map((r, i) => (
              <div key={i} className="px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  {/* Fix AI (2026-09-04): descripción completa, en varias líneas. */}
                  <p className="font-semibold text-gray-800 whitespace-normal break-words" title={r.descripcion}>{r.descripcion}</p>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500 flex-wrap">
                    <span>{r.unidad}</span>
                    {r.marca && <><span>·</span><span className="font-bold text-purple-700">{r.marca}</span></>}
                    {r.categoria && <><span>·</span><span className="text-emerald-700">{r.categoria}</span></>}
                    {r.codigo_proveedor && <><span>·</span><span className="font-mono">{r.codigo_proveedor}</span></>}
                    <span>·</span>
                    <span className="text-[#2E6DA4]">{r.proveedor}</span>
                  </div>
                </div>
                <p className="font-bold text-[#2E6DA4] tabular-nums whitespace-nowrap">{fmtPYG(r.precio_iva_inc)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lists */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-800">Listas de precios</h2>
          <button
            onClick={loadLists}
            className="text-gray-400 hover:text-[#2E6DA4] p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
            title="Recargar"
          >
            <RefreshCw size={14} />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-[#2E6DA4]" />
          </div>
        ) : lists.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Database size={36} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">Aún no hay listas de precios</p>
            <p className="text-xs text-gray-400 mt-1">Importá tu primer Excel o PDF para empezar</p>
            <button
              onClick={() => setImportOpen(true)}
              className="mt-4 inline-flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl px-4 py-2 text-xs font-semibold"
            >
              <Plus size={13} /> Importar lista
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Proveedor / Marca</th>
                <th className="px-5 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Ítems</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Última actualización</th>
                <th className="px-5 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lists.map((l) => (
                <tr key={l.proveedor} className="border-b border-gray-50 hover:bg-[#EBF3FB]/40 transition-colors" data-testid={`pricelist-row-${l.proveedor}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#EBF3FB] flex items-center justify-center">
                        <Building2 size={14} className="text-[#2E6DA4]" />
                      </div>
                      <p className="font-semibold text-gray-800">{l.proveedor}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-bold text-gray-700 tabular-nums">{l.item_count || 0}</td>
                  <td className="px-5 py-3 text-xs text-gray-500">{l.updated_at ? l.updated_at.slice(0, 10) : "—"}</td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button
                      onClick={() => { setDrawerProveedor(l.proveedor); setDrawerOpen(true); }}
                      className="inline-flex items-center gap-1 bg-[#2E6DA4]/10 hover:bg-[#2E6DA4]/20 text-[#2E6DA4] rounded-lg px-3 py-1.5 text-xs font-semibold"
                      data-testid={`view-list-btn-${l.proveedor}`}
                    >
                      <Search size={11} /> Ver ítems
                    </button>
                    <button
                      onClick={() => handleDelete(l.proveedor)}
                      className="inline-flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg px-3 py-1.5 text-xs font-semibold"
                      data-testid={`delete-list-btn-${l.proveedor}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={loadLists} />
      <ItemsDrawer open={drawerOpen} proveedor={drawerProveedor} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}
